import { ApolloClient, gql } from '@apollo/client';
import type {
  FormFileTransport,
  ResolvedFile,
} from '@eaco/forms-ui-web';

const ATTACHMENT_QUERY = gql`
  query portalAttachment($id: ID!, $download: Boolean!) {
    attachment(id: $id) {
      id
      name
      downloadLink(download: $download)
      thumbnailUrl
      fileSize
    }
  }
`;

/**
 * A read-only FormFileTransport for the document portal: it resolves an
 * attachment's signed URLs through the page's authed Apollo client so the
 * shared file/image/audio/video renderers work in the portal render (and the
 * PDF). Upload/design-time methods are unused in read-only mode.
 *
 * Only for a host with a session (the preview render). `attachment(id)`
 * answers null to anyone else, so a publicly shared page uses
 * makePublicPageFileTransport below.
 */
export function makePortalFileTransport(
  client: ApolloClient<unknown>,
): FormFileTransport {
  const resolve = async (
    fileId: number,
    download: boolean,
  ): Promise<ResolvedFile | null> => {
    if (!fileId) return null;
    try {
      const { data } = await client.query({
        query: ATTACHMENT_QUERY,
        variables: { id: String(fileId), download },
        fetchPolicy: 'network-only',
      });
      const a = (data as { attachment?: unknown })?.attachment as
        | {
            name?: string | null;
            downloadLink?: string | null;
            thumbnailUrl?: string | null;
            fileSize?: string | null;
          }
        | null
        | undefined;
      if (!a) return null;
      return {
        name: a.name ?? null,
        url: a.downloadLink ?? null,
        thumbnailUrl: a.thumbnailUrl ?? null,
        sizeLabel: a.fileSize ?? null,
      };
    } catch {
      return null;
    }
  };

  return {
    createPresignedUpload() {
      return Promise.reject(new Error('read-only transport'));
    },
    fetchResultFileUrl(fileId, opts) {
      return resolve(fileId, opts?.download ?? false);
    },
    fetchPublicFileUrl(fileId) {
      return resolve(fileId, false);
    },
  };
}

const PUBLIC_PAGE_FILE_QUERY = gql`
  query publicPageFile(
    $publicSlug: String!
    $password: String
    $fileId: ID!
    $download: Boolean!
  ) {
    publicPageFile(
      publicSlug: $publicSlug
      password: $password
      fileId: $fileId
    ) {
      id
      name
      mimeType
      sizeBytes
      downloadLink(download: $download)
      thumbnailUrl
    }
  }
`;

function sizeLabel(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * The read-only transport for a publicly shared page (pages.eaco.me/{slug}).
 *
 * The reader has no session, so a file is asked for through the page that
 * shows it: the slug, and the password when the page has one, are presented
 * with every lookup and the server decides from them. A file the page does
 * not vouch for resolves to null and the block renders its unavailable state.
 */
export function makePublicPageFileTransport(
  client: ApolloClient<unknown>,
  publicSlug: string,
  password: string | null,
): FormFileTransport {
  const resolve = async (
    fileId: number,
    download: boolean,
  ): Promise<ResolvedFile | null> => {
    if (!fileId || !publicSlug) return null;
    try {
      const { data } = await client.query({
        query: PUBLIC_PAGE_FILE_QUERY,
        variables: { publicSlug, password, fileId: String(fileId), download },
        // Signed URLs expire, so never answer from the cache.
        fetchPolicy: 'network-only',
      });
      const f = (data as { publicPageFile?: unknown })?.publicPageFile as
        | {
            name?: string | null;
            mimeType?: string | null;
            sizeBytes?: number | null;
            downloadLink?: string | null;
            thumbnailUrl?: string | null;
          }
        | null
        | undefined;
      if (!f) return null;
      return {
        name: f.name ?? null,
        url: f.downloadLink ?? null,
        thumbnailUrl: f.thumbnailUrl ?? null,
        mimeType: f.mimeType ?? null,
        sizeBytes: f.sizeBytes ?? null,
        sizeLabel: sizeLabel(f.sizeBytes),
      };
    } catch {
      return null;
    }
  };

  return {
    createPresignedUpload() {
      return Promise.reject(new Error('read-only transport'));
    },
    fetchResultFileUrl(fileId, opts) {
      return resolve(fileId, opts?.download ?? false);
    },
    fetchPublicFileUrl(fileId) {
      return resolve(fileId, false);
    },
  };
}
